<template>
  <div :class="{ desktop: isDesktop }" class="specific-container">
    <el-select
      v-model="model.id"
      class="platform-selector"
      @update:modelValue="updatePlatform"
    >
      <el-option
        v-for="item in platformOptions"
        :key="item.key"
        :label="item.label"
        :value="item.key"
      />
    </el-select>
    <el-select
      v-model="model.comparison"
      class="comparison-selector"
      @update:modelValue="updateComparison"
    >
      <el-option
        v-for="item in comparisonOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </el-select>
    <el-input
      v-model="model.version"
      class="version-input"
      @update:modelValue="updateVersion"
    />
    <el-button
      class="remove-button"
      icon="DeleteElementIcon"
      plain
      circle
      @click.prevent="removeSpecific"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import {
  PlatformLabels,
  PlatformLabel,
  Platform,
  SpecificVersion,
  VersionComparisonOptions,
  VersionComparison
} from '@/api/types'
import useResize from '@/hooks/useResize'

type PlatformEntry = {
  key: Platform
  label: string
  disabled: boolean
}

export default defineComponent({
  name: 'SpecificPlatformSelector',
  props: {
    platform: {
      type: Object,
      required: true
    },
    availablePlatforms: {
      type: Array,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  setup(props, context) {
    const model = ref(props.platform)
    const platformOptions = computed(() => {
      return PlatformLabels.filter(
        (label: PlatformLabel) =>
          label.type_id === props.platform.id ||
          props.availablePlatforms.indexOf(label.type_id) !== -1
      ).map<PlatformEntry>((label: PlatformLabel) => {
        return {
          key: label.type_id,
          label: label.label,
          disabled: false
        }
      })
    })

    function updatePlatform(newPlatform: Platform) {
      const newSpecific: SpecificVersion<Platform> = {
        id: newPlatform,
        version: props.platform.version,
        comparison: props.platform.comparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function updateComparison(newComparison: VersionComparison) {
      const newSpecific: SpecificVersion<Platform> = {
        id: props.platform.id,
        version: props.platform.version,
        comparison: newComparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function updateVersion(newVersion: string) {
      const newSpecific: SpecificVersion<Platform> = {
        id: props.platform.id,
        version: newVersion,
        comparison: props.platform.comparison
      }
      context.emit('update', { index: props.index, specific: newSpecific })
    }

    function removeSpecific() {
      context.emit('remove', props.index)
    }

    const comparisonOptions = VersionComparisonOptions

    const { isDesktop } = useResize()

    return {
      model,
      platformOptions,
      updatePlatform,
      updateComparison,
      updateVersion,
      removeSpecific,
      comparisonOptions,
      isDesktop
    }
  }
})
</script>

<style lang="scss" scoped>
.specific-container {
  margin-bottom: 0.5em;
}

.platform-selector {
  width: 120px;
}
.desktop .platform-selector {
  width: 180px;
}

.comparison-selector {
  width: 65px;
}

.desktop .comparison-selector {
  width: 70px;
  margin: 0 0.5em;
}

.version-input {
  width: 100px;
}
.desktop .version-input {
  width: 180px;
}

.remove-button {
  margin-left: 0.5em;
}
.desktop .remove-button {
  margin-left: 1.5em;
}
</style>
